import { LatLng } from "leaflet"

/**
 * This is the object retrieved from the project series settings, the series themselves can
 * have multiple properties that are populated after data is taken from the server
 */
export interface ISeriesInfo {
    /**
     * States if the wind atmosphere data is modeled or not
     */
    isWindAtmosphereModeled: boolean,
    /**
     * States if the hidrography data is modeled or not
     */
    isHydrographyModeled: boolean,
    /**
     * States if the waves data is modeled or not
     */
    isWavesModeled: boolean,
    /**
     * Base velocity unit used for the wind values
     */
    velocityUnit: string,
    /**
     * The widgets associated with the wind & atmosphere data
     */
    windAndAtmosphereWidgets: IWidget,
    /**
     * The widgets associated with the hidrography data
     */
    hidrographyWidgets: IWidget,
    /**
     * The widgets associated with the waves data
     */
    wavesWidgets: IWidget
}

/**
 * It represents a widget with both the charts and last values data needed to create it
 */
export interface IWidget {
    /**
     * Chart object has the properties to create a time series chart
     */
    charts: IChart,
    /**
     * Tables object are the series that will be displayed in tables
     */
    tables: ISerie[],
    /**
     * Last values are the configuration used to get the last values for a determined parameter
     */
    lastValues: ISerie[]
}

/**
 * This object states the common properties of a chart
 */
export interface IChart {
    /**
     * The y axis configurations, a collection of it that can associate multiple time series to
     * one y axis
     */
    yAxis: IChartYAxis[]
}

/**
 * Common y axis chart options and the series associated with the axis
 */
export interface IChartYAxis {
    /**
     * The name that will be displayed in the y axis
     */
    name: string,
    /**
     * The minimum value for the y axis
     */
    min: number,
    /**
     * The maximum value for the y axis
     */
    max: number,
    /**
     * Sets the tick interval to the value requested
     */
    tickInterval: number | "dynamic",
    /**
     * A collection of series
     */
    series: ISerie[]
}

/**
 * Custom chart options when using multiple series in the same chart
 */
export interface IChartOptions {
    /**
     * The color for the chart values
     */
    color: string,
    /**
     * States if the chart should be are or line
     */
    isFilled: boolean,
    /**
     * Can have little dots on each value
     */
    hasDots: boolean,
    /**
     * If is line chart, the dash style can be customized
     */
    dashStyle: string | undefined,
    /**
     * States the type of symbol when using dots
     */
    symbol: "square" | "circle" | "diamond" | "triangle" | undefined,
    /**
     * Z index property
     */
    zIndex: number,
}

/**
 * This object is populated with information after getting the metadata and values from the server
 */
export interface ISerie {
    /**
     * The ID of the time serie
     */
    id: number,
    /**
     * The description for the time series
     */
    description: string,
    /**
     * States the position and makes it easy to search the data
     */
    indexFrontend: IndexFrontend,
    /**
     * The aquasafe server to get the data from
     */
    server: string,
    /**
     * The station code where tha values come from
     */
    stationCode?: string,
    /**
     * The location name where the values come from
     */
    locationName?: string,
    /**
     * The location latitude and longitude
     */
    locationCoords?: LatLng,
    /**
     * The minimum value acceptable for this variable
     */
    min?: number,
    /**
     * The maximum value acceptable for this variable
     */
    max?: number,
    /**
     * Can customize the number of decimal places
     */
    decimalPlaces?: number
    /**
     * Source name of the values
     */
    sourceName?: string,
    /**
     * The parameter name of the values
     */
    parameterName?: string,
    /**
     * The unit of the parameter
     */
    unit?: string,
    /**
     * The chart custom options
     */
    chartOptions?: IChartOptions,
    /**
     * States if the data is tide or not
     */
    isTide?: boolean,
    /**
     * States if data is tide surge or not
     */
    isTideSurge?: boolean,
    /**
     * States if data is weather data
     */
    isWeather?: boolean,
    /**
     * The last value of the series
     */
    lastValue?: number,
    /**
     * The date of the last value
     */
    date?: Date,
    /**
     * The communication tolerance time interval in seconds that may consider the value
     * source faulty
     */
    communicationTolerance?: number,
    /**
     * Possible chart values if series is supposed to display as chart
     */
    chartValues?: IChartValue[],
    /**
     * States if is measure or modeled time series
     */
    isMeasure: boolean,
    /**
     * The hindcast visible in hours
     */
    visibleHindcastInHours?: number
}

/**
 * It represents a chart time serie value
 */
export interface IChartValue {
    /**
     * Can have the series id
     */
    id: number,
    /**
     * The value
     */
    value: number,
    /**
     * The date for the time serie value
     */
    date: Date
}
  
/**
 * Metadata of a time serie
 */
export interface IMetadata {
    /**
     * The ID of the serie
     */
    id: number;
    /**
     * Station code, represents a harbor station
     */
    stationCode: string;
    /**
     * The location name where the serie data comes from
     */
    locationName: string;
    /**
     * Source name of the serie
     */
    sourceName: string;
    /**
     * The parameter name of the time series
     */
    parameterName: string;
    /**
     * The unit of the time series
     */
    unit: string;
}

/**
 * The enum values in this enum is used for easy access and readability.
 */
export enum IndexFrontend {
    // Wind and Atmoshpere index frontend positions
    TemperatureLastValue = 1.1,
    AtmosphericPressureLastValue = 1.2,
    PrecipitationLastValue = 1.3,
    RelativeHumidityLastValue = 1.4,
    WindDirectionLastValue = 1.5,
    WindGustLastValue = 1.6,
    WindIntensityLastValue = 1.7,
    WeatherStateTable = 1.8,
    TemperatureChart = 1.9,
    PrecipitationChart = 1.11,
    WindIntensityChartAndTable = 1.12,
    WindDirectionTable = 1.13,
    MaxTemperatureLast24Hours = 1.14,
    MinTemperatureLast24Hours = 1.15,
    VisabilityLastValue = 1.16,
    
    // Hidrography index frontend positions
    SeaLevelLastValue = 2.1,
    TideTable = 2.2,
    CurrentDirectionLastValue = 2.3,
    CurrentIntensityLastValue = 2.4,
    SeaLevelChart = 2.5,
    CurrentIntensityChart = 2.6,
    SubElevationTideTable = 2.7,

    // Waves index frontend positions
    MeanWaveDirectionLastValue = 3.1,
    MeanPeakPeriodLastValue = 3.2,
    WavesHeightLastValue = 3.4,
    PeakPeriodLastValue = 3.5,
    MaxWaveHeightLastValue = 3.6,
    WaterTemperatureLastValue = 3.7,
    MeanPeakPeriodChart = 3.8,
    WavesHeightChart = 3.9
}